import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  History, Trash2, RefreshCw, Eye, AlertTriangle, FileText, Loader2,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  uploaded: 'bg-blue-100 text-blue-700',
  processed: 'bg-emerald-100 text-emerald-700',
  partially_processed: 'bg-amber-100 text-amber-700',
  failed: 'bg-red-100 text-red-700',
  deleted: 'bg-slate-200 text-slate-600 line-through',
};

const fmtDate = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleString(); } catch { return s; } };

export default function AttendanceUploadHistory() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [filters, setFilters] = useState({ company_id: '', month: '', source_type: '', status: '' });
  const [viewBatch, setViewBatch] = useState(null);
  const [deleteBatch, setDeleteBatch] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);


  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
        setCompanies(Array.isArray(data) ? data : (data.companies || []));
      } catch { /* */ }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
      const { data } = await axios.get(`${API_URL}/attendance/v2/batches?${params}`, { withCredentials: true });
      setBatches(data.batches || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [filters, load]);

  const handleDelete = async () => {
    if (!reason.trim() || reason.trim().length < 3) {
      toast.error('Please enter a reason (3+ chars)'); return;
    }
    setBusy(true);
    try {
      await axios.delete(`${API_URL}/attendance/v2/batches/${deleteBatch.id}`, {
        withCredentials: true,
        data: { reason: reason.trim() },
      });
      toast.success('Batch deleted');
      setDeleteBatch(null); setReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally { setBusy(false); }
  };

  const handleReprocess = async (b) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/attendance/v2/batches/${b.id}/reprocess`,
        {}, { withCredentials: true });
      toast.success(`Reprocessed — ${data.employees_calculated} employees, ${data.exceptions_count} exceptions`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reprocess failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="attendance-history-page">
      <div className="mb-6 flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <History className="text-blue-600" size={28} /> Upload History
          </h1>
          <p className="text-gray-600 mt-1">Every imported batch — view, delete, reprocess.</p>
        </div>
        <button onClick={load} className="text-slate-600 hover:text-slate-900 flex items-center gap-1 text-sm">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <select value={filters.company_id} onChange={e => setFilters({ ...filters, company_id: e.target.value })}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All Companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="month" value={filters.month}
            onChange={e => setFilters({ ...filters, month: e.target.value })}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
            placeholder="Month" />
          <select value={filters.source_type} onChange={e => setFilters({ ...filters, source_type: e.target.value })}
            data-testid="filter-source"
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All Sources</option>
            <option value="manual_upload">Manual Upload</option>
            <option value="biometric">Biometric</option>
            <option value="elite">Elite</option>
            <option value="api">API</option>
            <option value="internal">Internal</option>
          </select>
          <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All Statuses</option>
            <option value="processed">Processed</option>
            <option value="partially_processed">Partial</option>
            <option value="failed">Failed</option>
            <option value="deleted">Deleted</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? <div className="p-8 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>
          : batches.length === 0 ? <div className="p-10 text-center text-slate-500">No batches uploaded yet.</div>
            : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Batch #</th>
                    <th className="text-left px-4 py-3 font-medium">Source</th>
                    <th className="text-left px-4 py-3 font-medium">Month</th>
                    <th className="text-left px-4 py-3 font-medium">Records</th>
                    <th className="text-left px-4 py-3 font-medium">Uploaded By</th>
                    <th className="text-left px-4 py-3 font-medium">Uploaded At</th>
                    <th className="text-left px-4 py-3 font-medium">Status</th>
                    <th className="text-right px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map(b => (
                    <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`batch-row-${b.batch_number}`}>
                      <td className="px-4 py-3 font-mono text-blue-700">{b.batch_number}</td>
                      <td className="px-4 py-3 capitalize">{(b.source_type || '').replace('_', ' ')}</td>
                      <td className="px-4 py-3">{b.month || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="text-emerald-700 font-medium">{b.success_count || 0}</span>
                        {b.error_count > 0 && <span className="text-red-700 ml-2">/ {b.error_count} errors</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{b.uploaded_by_name}</td>
                      <td className="px-4 py-3 text-slate-500">{fmtDate(b.uploaded_at)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[b.status]}`}>
                          {(b.status || '').replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => setViewBatch(b)} title="View records"
                            data-testid={`view-${b.batch_number}`}
                            className="p-1.5 rounded hover:bg-blue-100 text-blue-600"><Eye size={14} /></button>
                          {!b.is_deleted && (
                            <>
                              <button onClick={() => handleReprocess(b)} disabled={busy} title="Reprocess"
                                data-testid={`reprocess-${b.batch_number}`}
                                className="p-1.5 rounded hover:bg-amber-100 text-amber-600 disabled:opacity-50"><RefreshCw size={14} /></button>
                              <button onClick={() => setDeleteBatch(b)} title="Delete batch"
                                data-testid={`delete-${b.batch_number}`}
                                className="p-1.5 rounded hover:bg-red-100 text-red-600"><Trash2 size={14} /></button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
      </div>

      {viewBatch && <RecordsModal batch={viewBatch} onClose={() => setViewBatch(null)} />}
      {deleteBatch && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDeleteBatch(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()} data-testid="delete-batch-modal">
            <div className="flex items-center gap-2 mb-3 text-red-700">
              <AlertTriangle size={22} /> <h3 className="font-bold text-lg">Delete attendance batch?</h3>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              <b>{deleteBatch.batch_number}</b> — {deleteBatch.success_count} attendance records will be permanently removed
              for <b>{deleteBatch.month || '(unknown month)'}</b>.
            </p>
            <p className="text-xs text-slate-500 mb-3">
              This action will be blocked if attendance is locked or payroll has been processed for this month.
              Employee master, biometric mappings, leave policies and payroll data are NOT touched.
            </p>
            <label className="text-sm font-medium text-slate-700">Reason *</label>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
              data-testid="delete-reason"
              className="w-full mt-1 border border-slate-300 rounded-lg px-3 py-2 text-sm"
              placeholder="e.g. Test data uploaded for April — to be reuploaded with correct file" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setDeleteBatch(null)} className="px-4 py-2 rounded-lg bg-slate-100 text-slate-700 text-sm">Cancel</button>
              <button onClick={handleDelete} disabled={busy}
                data-testid="confirm-delete"
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium disabled:opacity-50">
                {busy ? 'Deleting…' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RecordsModal({ batch, onClose }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/attendance/v2/batches/${batch.id}/records?limit=500`,
          { withCredentials: true });
        setRecords(data.records || []);
      } catch { /* */ } finally { setLoading(false); }
    })();
  }, [batch.id]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex justify-between items-center">
          <h2 className="font-bold flex items-center gap-2"><FileText size={18} /> Records — {batch.batch_number}</h2>
          <button onClick={onClose} className="text-slate-400 text-2xl">×</button>
        </div>
        <div className="overflow-y-auto p-2">
          {loading ? <div className="p-6 text-center"><Loader2 className="animate-spin inline" /></div>
            : records.length === 0 ? <div className="p-6 text-slate-500 text-center">No records.</div>
              : (
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-2 py-1.5">Code</th>
                      <th className="text-left px-2 py-1.5">Date</th>
                      <th className="text-left px-2 py-1.5">Status</th>
                      <th className="text-left px-2 py-1.5">In</th>
                      <th className="text-left px-2 py-1.5">Out</th>
                      <th className="text-left px-2 py-1.5">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-2 py-1 font-mono">{r.employee_code}</td>
                        <td className="px-2 py-1">{r.attendance_date}</td>
                        <td className="px-2 py-1 capitalize">{(r.attendance_status || '').replace('_', ' ')}</td>
                        <td className="px-2 py-1">{r.in_time || ''}</td>
                        <td className="px-2 py-1">{r.out_time || ''}</td>
                        <td className="px-2 py-1 text-slate-500">{r.remarks || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
        </div>
      </div>
    </div>
  );
}
